// Style tokens for JobSection (2026-09-11; tiles 2026-09-29, RD-LOG-005) —
// the shell for a job section: NAME + figure, then its ITEMS as rounded
// tinted tiles. The 'card' variant holds the header INSIDE the white card;
// the older 'canvas' variant puts the header on the page canvas above it.
// Tokens only — the only raw literals are '-0.015em' (letter-spacing, no
// token exists), 1.2 (lineHeight), the 44px touch-target min-height, and
// HEADER_BORDER_BOTTOM_OFF (0px transparent — UA-chrome pin, not a token).
import { foundation } from '../../ui';

const fc = foundation.color;
const sp = foundation.spacePx;
const fs = foundation.fontSize;
const fw = foundation.fontWeight;
const rd = foundation.radii;
const sh = foundation.shadow;

// Header bottom edge. Must be a concrete longhand (not omitted, not `none`)
// so WebKit cannot restore native <button> chrome (a dark underline on the
// pill) — it stays pinned even though no variant draws a divider now.
// See JobSection.js.
const HEADER_BORDER_BOTTOM_OFF = '0px solid transparent';

/** The one gap between stacked job cards (a section's marginBottom) - wrap any non-section row in the stack with it. */
export const sectionStackGap = (layout) => (layout === 'phone' ? sp[5] : sp[6]);

/**
 * Returns every static (layout-dependent, not state-dependent) style object
 * JobSection needs. Runtime-only pieces (animated height/overflow on the
 * disclosure wrapper, the chevron's rotation) are computed in the component
 * itself since they depend on `open`/`settled`, not on `layout`.
 *
 * @param {'phone' | 'tablet' | 'desktop'} layout
 * @param {'canvas' | 'card'} [variant] - 'canvas' (default) keeps the
 *   18px-phone/20px-desktop header (header on the page canvas; no caller
 *   left). 'card' pins the header to a flat 18px at EVERY width — it sits one
 *   line below the host drawer/panel's own 18px bold title, so it must read
 *   as a step down from that, never match or exceed it; do not reinterpret
 *   this back to 20px on desktop.
 */
export function sectionStyles(layout, variant = 'canvas') {
  const isPhone = layout === 'phone';
  const isCardVariant = variant === 'card';
  const headerFontSize = isCardVariant ? fs.lg : (isPhone ? fs.lg : fs.xl);

  return {
    root: {
      // Just breathing room above a jumped-to section — NOT header clearance.
      // The compact header and the jump strip are flex SIBLINGS of the
      // scroller, not overlays on top of it, so nothing covers the scrollport's
      // top edge and no compensation is owed. The old '100px' was the same
      // dead compensation that had the jump strip declaring a sticky offset it
      // could never use; it parked every jumped-to section 100px below the top
      // (2026-09-11).
      scrollMarginTop: sp[3],
      marginBottom: sectionStackGap(layout),
    },

    header: {
      display: 'flex',
      alignItems: 'baseline',
      gap: sp[2],
      background: 'none',
      border: 'none',
      // Pin a concrete longhand on every render. Omitting `borderBottom` lets
      // WebKit restore native <button> chrome — a dark underline on the
      // collapsed pill (JobDetail / LogJobDrawer, 2026-09-18). `none` is
      // dropped by jsdom and re-opens the UA rim once a longhand has been
      // set; 0px transparent stays pinned (no variant draws a divider now).
      borderBottom: HEADER_BORDER_BOTTOM_OFF,
      appearance: 'none',
      WebkitAppearance: 'none',
      MozAppearance: 'none',
      outline: 'none',
      // Horizontal padding MATCHES the card's insets below (sp[4] phone /
      // sp[5] else — `cardInset`, `addZone`) so the section NAME shares a
      // left edge with the content. The tiles sit one step inside that
      // (`tilesRegion`), so their tint reads as nested in the card. Keep
      // these in step (founder, 2026-09-11).
      padding: isPhone ? `${sp[3]} ${sp[4]}` : `${sp[3]} ${sp[5]}`,
      width: '100%',
      minHeight: '44px',
      cursor: 'pointer',
      textAlign: 'left',
      font: 'inherit',
      WebkitTapHighlightColor: 'transparent',
    },

    // Non-interactive (read-only) header: same geometry, none of the
    // affordances — nothing here is clickable.
    headerStatic: {
      cursor: 'default',
      WebkitTapHighlightColor: 'transparent',
    },

    // Always spread onto the header. Tiles are separated by gap + tint, so no
    // variant draws a divider under the header any more. React will not clear
    // a previously-set longhand unless the next style object still names it —
    // a 0-width transparent edge is the pin (jsdom also drops
    // `border-bottom: none`, so a concrete value is the only assertion that
    // can catch a regression to omitting the property).
    headerDividerOff: {
      borderBottom: HEADER_BORDER_BOTTOM_OFF,
    },

    name: {
      fontWeight: fw.semibold,
      letterSpacing: '-0.015em',
      color: fc.neutral.text,
      lineHeight: 1.2,
      fontSize: headerFontSize,
      minWidth: 0,
    },

    // The ONE right-aligned figure — never rendered alongside a count badge.
    figure: {
      marginLeft: 'auto',
      fontWeight: fw.bold,
      fontVariantNumeric: 'tabular-nums',
      color: fc.neutral.text,
      fontSize: headerFontSize,
      flexShrink: 0,
    },

    // A subtotal that cannot be totalled yet — "2 extras need a price". Same
    // slot as a real figure, in the warning tone so it never reads as a
    // settled amount, and deliberately at BODY size rather than the header's
    // 18/20px: it is a sentence, not a number, and at figure size it would
    // out-shout the section name it sits beside.
    figureWarning: {
      marginLeft: 'auto',
      fontWeight: fw.semibold,
      color: fc.semantic.warningText,
      fontSize: fs.base,
      flexShrink: 0,
      textAlign: 'right',
    },

    // "Opcional" / "Ninguna" — same slot, muted+smaller instead of a value.
    figureMuted: {
      marginLeft: 'auto',
      fontWeight: fw.medium,
      color: fc.neutral.textMuted,
      fontSize: fs.base,
      flexShrink: 0,
    },

    chevron: {
      color: fc.neutral.textMuted,
      marginLeft: sp[2],
      alignSelf: 'center',
      flexShrink: 0,
    },

    // Outer disclosure wrapper — animated height/overflow/transition are
    // merged in at render time by the component (runtime state, not tokens).
    bodyWrap: {
      transform: 'translateZ(0)',
      backfaceVisibility: 'hidden',
      overflowAnchor: 'none',
    },

    // flow-root so scrollHeight measurement includes the card's own margins.
    bodyInner: {
      display: 'flow-root',
    },

    // The card carries NO padding of its own: each child region supplies its
    // inset (`tilesRegion` for line-item tiles, `cardInset` for a form
    // control, `addZone` for the add button), so the header, the tiles and the
    // add zone each own their spacing.
    card: {
      backgroundColor: fc.neutral.surface,
      border: `1px solid ${fc.neutral.border}`,
      borderRadius: rd.lg,
      boxShadow: sh.xs,
      overflow: 'hidden',
    },

    // Wrapper for any card content that is NOT a tiles list — the notes
    // textarea, the photo grid, the date control. Supplies the inset the card
    // no longer does.
    cardInset: {
      padding: isPhone ? sp[4] : sp[5],
    },

    // The quick-add + "Agregar X" zone, below the tiles. Same horizontal
    // inset as `cardInset` so the button lines up with the header. Top is 0 —
    // `tilesRegion` already pads its bottom — so the gap above the button
    // stays one sp[3] (same as before RD-LOG-005's bottom-pad hole). Bottom +
    // sides supply the card's closing edge when the add button is present.
    addZone: {
      padding: `0 ${isPhone ? sp[4] : sp[5]} ${sp[3]}`,
    },

    // The line-item list: rounded tinted TILES (LineItemRow / LineItemRowExtra
    // with `tile`) separated by a small gap — no hairlines (RD-LOG-005,
    // 2026-09-29). Equal pad on all sides so a read-only card (no add zone)
    // keeps the last tile off the bottom edge; edit mode's addZone then
    // only adds sides + bottom under the button (2026-09-29).
    tilesRegion: {
      display: 'flex',
      flexDirection: 'column',
      gap: sp[2],
      padding: `${sp[3]} ${isPhone ? sp[3] : sp[4]} ${sp[3]}`,
    },
  };
}

export default sectionStyles;
